/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any --
 * Guide/export payloads and pcg-core are loose-typed by design. */

import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom"
import moment from "moment"
import { Alert, Box, Button, Stack, Typography } from "@mui/material"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import { examsBasePath } from "const"
import Page from "@medaica/common/components/page/page"
import Loading from "@medaica/common/components/loading"
import useGlobalContext from "@medaica/common/hooks/global-context"
import useInterfaceSize from "@medaica/common/hooks/interface-size"
import { Exam } from "@medaica/common/types"
import { logError } from "@medaica/common/services/util"
import { useSnackbar } from "notistack"
import useAuth from "hooks/auth"
import useBackLink, { openedFromState } from "hooks/back-link"
import {
  PcgRecordingViewer,
  createPcgCore,
  buildExamTimingConsistency,
  readTooltipPreference,
} from "@medaica/common/components/pcg-viewer"
import "@medaica/common/components/pcg-viewer/pcg-exam-workspace.css"
import usePcgPlaybackFilter, {
  PcgAudioGraph,
} from "@medaica/common/components/pcg-viewer/audio/use-pcg-playback-filter"
import PcgFilterControls from "@medaica/common/components/pcg-viewer/audio/pcg-filter-controls"
import type { PluginId } from "@medaica/common/components/pcg-viewer/plugins/plugin-definitions"
import {
  PcgDispositionRequest,
  PcgHeartReportBundleSummary,
  PcgHeartReportRelease,
  PcgPluginReview,
  PcgReviewBundle,
  deriveDispositionExportValue,
  recordingSiteLabel,
} from "@medaica/common/services/medaica-api-service/pcg-review-types"
import {
  PcgExportPayloadRejectedError,
  PcgPatientReportNotReleasedError,
} from "@medaica/common/services/medaica-api-service/PcgReviewApi"
import { MedaicaApiErrorType } from "@medaica/common/services/medaica-api-service"
import { buildPipelineMetrics } from "@medaica/common/components/pcg-viewer/pipeline-metrics"
import {
  ACCEPTED_WITHOUT_EVIDENCE_MESSAGE,
  NO_ACCEPTED_RECORDINGS_MESSAGE,
} from "@medaica/common/components/pcg-report/core"
import downloadBlob from "@medaica/common/utils/download-blob"
import { apiErrorTypeOf } from "./patient-delivery"
import RecordingRail from "./components/recording-rail"
import DispositionControls from "./components/disposition-controls"
import {
  acceptedEvidenceSummary,
  describeAcceptEvidence,
  EvaluationRangeProvenance,
  PackageEvidence,
} from "./components/accept-evidence"
import PluginRack from "./components/plugin-rack"
import ExamOverviewMap, { aptmLayoutFits } from "./components/exam-overview-map"
import ExamFacts from "./components/exam-facts"
import { backLinkSx } from "./back-link"
import { examDemographics } from "./exam-demographics"
import PatientContextStrip from "./components/patient-context-strip"
import SelectedRecordingNav, { recordingReviewState } from "./components/selected-recording-nav"
import { buildRecordingNoteExports, examNotesSummary } from "./components/reviewer-observations"

/**
 * The PCG exam review workspace: the PcgRecordingViewer (waveform/CWT/guide
 * cards, review row, research panel) mounted inside the handoff v0.8.3 exam
 * layout — sidebar (Exam Overview map, Patient Context, Plugins) plus the
 * `.aptm-wave-main` column — and driven by the medaica-service pcg-review
 * client.
 *
 * State ownership (the integration contract):
 * - This page owns everything persisted: the bundle, dispositions, exports.
 * - The viewer owns everything ephemeral: pan/zoom, layers, playback, and the
 *   page's single AudioContext (reported up via onAudioGraph so the listening
 *   filter can splice into it).
 * - Exam-level timing consensus is derived here from all guide-ready payloads
 *   (client-side owner) and fed both to the viewer and the export.
 *
 * "Open Medaica Report" snapshots the current review as an export (the server
 * returns the existing export unchanged when nothing has changed) and then
 * navigates to the Medaica Report page, which works from that snapshot.
 *
 * Once the Medaica Report is released, a status strip below the workspace
 * shows the delivery facts of the release (who it was emailed to, when, the
 * email's status, when the link expires) and offers to re-send the email or
 * download the frozen patient report — and the frozen provider report, the
 * clinician-format document the patient receives to share with their own
 * doctor, when the release has one. An interim panel below it still
 * renders a fresh patient-report PDF on demand — a stand-in until patient
 * delivery exists in the Client Portal.
 */

/**
 * Which serialized turbulent-energy model a guide's features were produced
 * for — stamped into the payload by the analysis pipeline, resolved here
 * against the API's model-artifact store. Older payloads without the stamp
 * resolve to null, which the viewer surfaces as its explicit
 * "model unavailable" state (fallback scoring is disallowed).
 */
const turbulentModelVersionOf = (payload: unknown): string | null => {
  const version = (payload as { turbulentModelVersion?: unknown } | null)?.turbulentModelVersion
  return typeof version === "string" ? version : null
}

const SENT_AT_FORMAT = "MMM D, YYYY HH:mm"

// A status strip with action buttons on the right. When the row gets tight
// the buttons keep their labels on one line and the whole button group moves
// under the message, instead of each label wrapping inside its button.
const ACTION_STRIP_SX = {
  mt: 2,
  alignItems: "center",
  flexWrap: "wrap",
  "& .MuiAlert-message": { flex: "1 1 18rem" },
  "& .MuiAlert-action": { alignItems: "center", pt: 0, mr: 0 },
  "& .MuiButton-root": { whiteSpace: "nowrap" },
}

/**
 * The delivery facts of a release for the status line: "emailed to
 * j•••@example.com on Sep 14, 2026 10:32 (sent)".
 */
const deliverySummary = (release: PcgHeartReportRelease): string => {
  const sentAt = release.lastSentAt ? moment(release.lastSentAt).format(SENT_AT_FORMAT) : "—"
  return `emailed to ${release.recipientMasked} on ${sentAt} (${release.emailStatus ?? "not sent"})`
}

/**
 * The Medaica Report status line shown beside "Open Medaica Report": where the
 * report stands, whether the review has moved on since the snapshot the
 * report was derived from and, once released, where the report was sent.
 */
const heartReportStatusLabel = (
  heartReport: PcgHeartReportBundleSummary | null,
  anyRecordingAccepted: boolean
): string => {
  if (!heartReport) {
    // Nothing can reach a report until a recording is accepted: say how
    return anyRecordingAccepted
      ? "Medaica Report: not started"
      : "No Medaica Report evidence accepted yet · select Accept on a reviewed recording."
  }
  if (heartReport.status === "released") {
    const label = heartReport.reviewChangedSinceSource
      ? "Medaica Report: Released — review has changed since release"
      : "Medaica Report: Released"
    return heartReport.release ? `${label} · ${deliverySummary(heartReport.release)}` : label
  }
  if (heartReport.status === "approved") {
    return heartReport.reviewChangedSinceSource
      ? "Medaica Report: Approved — review has changed since approval"
      : "Medaica Report: Approved"
  }
  return heartReport.reviewChangedSinceSource
    ? "Medaica Report: In Review — will update from the latest review"
    : "Medaica Report: In Review"
}

/**
 * After a review write (a disposition) the saved Heart
 * Report, if any, no longer matches the review. The server recomputes this
 * on the next bundle load; flip it locally so the status line is right now.
 */
const withReviewChanged = (bundle: PcgReviewBundle): PcgReviewBundle =>
  bundle.heartReport ? { ...bundle, heartReport: { ...bundle.heartReport, reviewChangedSinceSource: true } } : bundle

/**
 * The released status strip's text: recipient, the email's status and send
 * time, and when the patient's download link expires.
 */
const releaseStripText = (release: PcgHeartReportRelease): string => {
  const emailStatus = release.emailStatus ?? "not sent"
  const sentAt = release.lastSentAt ? ` ${moment(release.lastSentAt).format(SENT_AT_FORMAT)}` : ""
  const linkExpiry = release.linkExpiresAt
    ? `Link expires ${moment(release.linkExpiresAt).format("MMM D, YYYY")}`
    : "Link expiry unknown"
  return `Released to ${release.recipientMasked} · Email ${emailStatus}${sentAt} · ${linkExpiry}`
}

/** Why re-sending the report email failed, in the reviewer's terms. */
const resendErrorMessage = (error: unknown): string => {
  if (error instanceof PcgPatientReportNotReleasedError) {
    return "Medaica Report is not released"
  }
  if (apiErrorTypeOf(error) === MedaicaApiErrorType.RATE_LIMITED) {
    return "An email was sent less than a minute ago — try again shortly"
  }
  return "Could not re-send the email"
}

/**
 * The patient the exam was for (frozen at booking when it was booked, not
 * the shared profile), under the same cross-org mask the page header applies.
 */

const ExamReviewView = (): ReactElement => {
  const { examId } = useParams<{ examId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  // Review routes exist under each exam base path (own exams and the two
  // organization exam lists); keep navigation on the one the user came in on
  const examsBase = examsBasePath(location.pathname)
  const backToExamLink = useBackLink(`${examsBase}/${examId ?? ""}`)
  const { medaicaApiService } = useGlobalContext()
  const { user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()

  const pcgReviewApi = useMemo(() => medaicaApiService.pcgReview, [medaicaApiService])

  const [exam, setExam] = useState<Exam | null>(null)
  // The top bar element the viewer portals its brand into (null until mounted)
  const [brandContainer, setBrandContainer] = useState<HTMLElement | null>(null)
  const [bundle, setBundle] = useState<PcgReviewBundle | null>(null)
  const [selectedAuscultationId, setSelectedAuscultationId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Storing the review snapshot can take several seconds before the page
  // navigates; the button shows a spinner for that whole stretch.
  const [openingHeartReport, setOpeningHeartReport] = useState(false)
  const [interfaceSizeLarge, toggleInterfaceSize] = useInterfaceSize("medaica-pcg-interface-size")
  // The viewer's Tips On / Off preference, which the body map's hover text follows
  const [tipsEnabled, setTipsEnabled] = useState<boolean>(readTooltipPreference)
  // A resend or download of a released report in flight
  const [deliveryBusy, setDeliveryBusy] = useState(false)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  // version → fetched model artifact, for every version the bundle's guides
  // name. Object identity is stable across renders (only replaced when the
  // named-version set changes), so the viewer's pcg-core memo holds.
  const [turbulentModels, setTurbulentModels] = useState<{ [version: string]: Record<string, unknown> }>({})

  // The viewer reports its Web Audio graph (one AudioContext, owned by the
  // wrapper); the listening-filter hook splices into it. The filter preset
  // lives here so it survives recording switches (viewer remounts).
  const [audioGraph, setAudioGraph] = useState<PcgAudioGraph | null>(null)
  const filterControls = usePcgPlaybackFilter(audioGraph)

  // Load exam via the existing API pattern, then the review bundle
  useEffect(() => {
    const fetchData = async () => {
      if (!medaicaApiService || !examId) {
        return
      }
      try {
        const loadedExam = await medaicaApiService.exams.getExamById(examId, ["patientProfile", "auscultations"])
        setExam(loadedExam)
        const loadedBundle = await pcgReviewApi.getReviewBundle(loadedExam)
        setBundle(loadedBundle)
        setSelectedAuscultationId((current) => current ?? loadedBundle.recordings[0]?.auscultationId ?? null)
      } catch (err) {
        logError(err)
        enqueueSnackbar("There was an error loading the review workspace", { variant: "error" })
      }
    }
    void fetchData()
  }, [enqueueSnackbar, examId, medaicaApiService, pcgReviewApi])

  // The distinct turbulent-model versions the bundle's guides name, as a
  // stable string key so the fetch effect below does NOT refire on every
  // bundle object change (dispositions/exports replace the bundle object).
  const neededTurbulentModelVersions = useMemo(() => {
    if (!bundle) {
      return ""
    }
    const versions = new Set<string>()
    bundle.recordings.forEach((recording) => {
      const version = turbulentModelVersionOf(recording.guide?.payload)
      if (version) {
        versions.add(version)
      }
    })
    return Array.from(versions)
      .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
      .join("\n")
  }, [bundle])

  // Fetch the model artifact for each named version. The endpoint is
  // version-addressed and immutable-cached, so refetches after a reload are
  // served from the browser cache. A version that fails to load stays absent
  // from the map — the affected panels render their explicit "model
  // unavailable" state rather than scoring with a different model.
  const fetchedTurbulentModelKey = useRef<string | null>(null)
  useEffect(() => {
    if (!neededTurbulentModelVersions || fetchedTurbulentModelKey.current === neededTurbulentModelVersions) {
      return
    }
    fetchedTurbulentModelKey.current = neededTurbulentModelVersions
    let cancelled = false
    const fetchModels = async () => {
      const loaded = await Promise.all(
        neededTurbulentModelVersions.split("\n").map(async (version) => {
          try {
            return [version, await pcgReviewApi.getModelArtifact(version)] as const
          } catch (err) {
            logError(err)
            return [version, null] as const
          }
        })
      )
      if (cancelled) {
        return
      }
      const models: { [version: string]: Record<string, unknown> } = {}
      loaded.forEach(([version, model]) => {
        if (model) {
          models[version] = model
        }
      })
      setTurbulentModels(models)
      if (loaded.some(([, model]) => !model)) {
        enqueueSnackbar("The turbulence scoring model could not be loaded — turbulence evidence is unavailable", {
          variant: "warning",
        })
      }
    }
    void fetchModels()
    return () => {
      cancelled = true
    }
  }, [enqueueSnackbar, neededTurbulentModelVersions, pcgReviewApi])

  // Load the selected recording's audio (dual-mono playback route: body
  // channel in both ears). The Blob is handed to the viewer, which owns
  // object-URL lifecycle and the <audio> element (and hence the one-shot
  // createMediaElementSource capture).
  useEffect(() => {
    let cancelled = false
    setAudioBlob(null)
    const fetchAudio = async () => {
      if (!medaicaApiService || !selectedAuscultationId) {
        return
      }
      try {
        const blob = await medaicaApiService.auscultations.getAuscultationRecordingPlaybackData(selectedAuscultationId)
        if (!cancelled) {
          setAudioBlob(blob)
        }
      } catch (err) {
        logError(err)
        if (!cancelled) {
          setAudioBlob(null)
        }
      }
    }
    void fetchAudio()
    return () => {
      cancelled = true
    }
  }, [medaicaApiService, selectedAuscultationId])

  const selectedRecording = bundle?.recordings.find((r) => r.auscultationId === selectedAuscultationId) ?? null

  // Site label of the selected recording as the workspace displays it —
  // the observations dialog title uses it.
  const selectedRecordingLabel = selectedRecording ? recordingSiteLabel(selectedRecording) : undefined
  // Across the exam, whichever recording is selected: every accepted
  // recording with what it was accepted for, and every recording's notes
  const reviewedRecordings = (bundle?.recordings ?? []).map((recording) => ({
    label: recordingSiteLabel(recording),
    disposition: recording.disposition,
  }))
  const acceptedEvidence = acceptedEvidenceSummary(reviewedRecordings)
  const examNotes = examNotesSummary(reviewedRecordings)

  // Exam-level timing consensus across all guide-ready recordings —
  // client-side owner of the handoff's _exam_timing_consistency.
  // Note: recomputed whenever the bundle object changes (incl. disposition
  // writes); it could be memoized on guide identity instead.
  const examTimingConsistency = useMemo(() => {
    if (!bundle) {
      return null
    }
    return buildExamTimingConsistency(
      bundle.recordings
        .filter((r) => r.guide?.status === "ok" && r.guide.payload)
        .map((r) => ({ recordingId: r.auscultationId, label: recordingSiteLabel(r), payload: r.guide?.payload }))
    )
  }, [bundle])

  const handleDisposition = useCallback(
    async (request: PcgDispositionRequest) => {
      if (!bundle || !selectedAuscultationId) {
        return
      }
      setBusy(true)
      try {
        const row = await pcgReviewApi.putDisposition(bundle.examId, selectedAuscultationId, request)
        setBundle((current) =>
          current
            ? withReviewChanged({
                ...current,
                recordings: current.recordings.map((recording) =>
                  recording.auscultationId === selectedAuscultationId ? { ...recording, disposition: row } : recording
                ),
              })
            : current
        )
      } catch (err) {
        logError(err)
        enqueueSnackbar("There was an error saving the disposition", { variant: "error" })
      } finally {
        setBusy(false)
      }
    },
    [bundle, enqueueSnackbar, pcgReviewApi, selectedAuscultationId]
  )

  // A plugin review is saved with the recording's disposition: the decision,
  // note, observations and domains go back unchanged, so only the plugin
  // review moves.
  const handlePluginReview = useCallback(
    (plugin: PluginId, review: PcgPluginReview) => {
      const disposition = selectedRecording?.disposition ?? null
      void handleDisposition({
        decision: disposition?.decision ?? "none",
        acceptedDomains: disposition?.acceptedDomains ?? null,
        rejectReasons: disposition?.rejectReasons ?? [],
        note: disposition?.note ?? "",
        observations: disposition?.observations ?? null,
        pluginReviews: {
          act: disposition?.pluginReviews?.act ?? null,
          beataware: disposition?.pluginReviews?.beataware ?? null,
          [plugin]: review,
        },
      })
    },
    [handleDisposition, selectedRecording]
  )

  // Snapshot the current review as an export, then open the Medaica Report
  // page on it. The server hands back the latest export unchanged when the
  // review has not moved since, so this is safe to run on every click.
  // One pcg-core evidence package for a guide-ready recording, built from the
  // CURRENT derived results (review status and the exam timing consensus baked
  // into the context). Shared by the export and the Accept dialog, so the
  // dialog shows exactly the evidence an export would carry.
  const buildRecordingEvidencePackage = useCallback(
    (recording: PcgReviewBundle["recordings"][number], recordingNotes: unknown[]): unknown => {
      if (!bundle || recording.guide?.status !== "ok" || !recording.guide.payload) {
        return null
      }
      try {
        const core = createPcgCore(recording.guide.payload as any, {
          turbulentModel: (turbulentModels[turbulentModelVersionOf(recording.guide.payload) ?? ""] ?? null) as any,
          examTimingConsistency,
          currentRecordingId: recording.auscultationId,
        })
        return core.buildCardiacReportEvidencePackage({
          recordingId: recording.auscultationId,
          examId: bundle.examId,
          // The recording's review state feeds the package's attestation
          // fields ("accepted" is exported as "attested").
          reviewStatus: recordingReviewState(recording),
          // The bundle's questionnaire rides into the context/contextInputs
          // blocks; null stays null (no demo substitution). The platform
          // supplies no patientContext, so it is always null.
          examContext: { questionnaire: bundle.questionnaire ?? null, patientContext: null, recordingNotes },
          // A BPM the reviewer confirmed in the Note dialog is this recording's
          // accepted heart rate only while the recording is accepted for
          // Timing (Medaica Viewer v0.9.1); the exam roll-up applies the same
          // rule per recording from the snapshot's dispositions.
          hcpConfirmedBpm:
            recording.disposition?.decision === "accepted" && recording.disposition.acceptedDomains?.timing !== false
              ? recording.disposition.observations?.confirmedBpm ?? null
              : null,
        })
      } catch (err) {
        logError(err)
        return null
      }
    },
    [bundle, examTimingConsistency, turbulentModels]
  )

  // What the Accept dialog offers for the selected recording: which evidence
  // domains it can contribute, with the values being accepted — read from the
  // same evidence package the export sends to the Report (a timing-engine
  // guide's timing from its Evaluation Range alone)
  const acceptEvidence = useMemo(() => {
    if (!selectedRecording || selectedRecording.guide?.status !== "ok") {
      return null
    }
    const evidencePackage = buildRecordingEvidencePackage(selectedRecording, []) as {
      evidence?: PackageEvidence
      evaluationRange?: EvaluationRangeProvenance
    } | null
    return describeAcceptEvidence(
      buildPipelineMetrics(selectedRecording.guide.payload),
      evidencePackage?.evidence ?? null,
      evidencePackage?.evaluationRange ?? null
    )
  }, [buildRecordingEvidencePackage, selectedRecording])

  const handleOpenHeartReport = useCallback(async () => {
    if (!bundle) {
      return
    }
    // No accepted recording, no report — the handoff viewer refuses the
    // export the same way ("Accept at least one recording before creating
    // the Report draft."). Checked here so that no snapshot is stored;
    // the report adapter repeats the rule for any snapshot that reaches it.
    const acceptedRecordings = bundle.recordings.filter((recording) => recordingReviewState(recording) === "accepted")
    if (acceptedRecordings.length === 0) {
      enqueueSnackbar(NO_ACCEPTED_RECORDINGS_MESSAGE, { variant: "warning" })
      return
    }
    if (!acceptedRecordings.some((recording) => recording.guide?.status === "ok" && recording.guide.payload)) {
      enqueueSnackbar(ACCEPTED_WITHOUT_EVIDENCE_MESSAGE, { variant: "warning" })
      return
    }
    setBusy(true)
    setOpeningHeartReport(true)
    try {
      // The export payload is built client-side from the CURRENT derived
      // results — one pcg-core evidence package per guide-ready recording
      // (with its review status and the exam
      // timing consensus baked into the context) plus the disposition rows
      // with the client-derived composite value. Patient-context inputs are
      // nulls. The exam-level envelope is a portal construct — the vendored
      // builder is single-recording scope; the exam-level export schema is
      // still a contract question. It satisfies the server's structural gate
      // via the `recordings` section, whose entries MUST carry `recordingId`
      // (export_validation.py).
      // Reviewer observations ride exam-wide in every evidence package's
      // examContext (the handoff's `__medaicaExamRecordingNotes`), so the
      // Medaica Report can import them from any package.
      const recordingNotes = buildRecordingNoteExports(bundle.examId, bundle.recordings)
      const recordings = bundle.recordings.map((recording) => {
        const evidencePackage = buildRecordingEvidencePackage(recording, recordingNotes)
        return {
          // The export row's stable key is the recording id (the platform's
          // auscultation id) — required by the server gate.
          recordingId: recording.auscultationId,
          auscultationId: recording.auscultationId,
          auscultationPoint: recording.auscultationPoint,
          site: recording.site ?? null,
          guideStatus: recording.guide?.status ?? "pending",
          guideVersion: recording.guide?.guideVersion ?? null,
          // The pipeline's whole-recording timing metrics (uncorrected): the
          // exam-level roll-up's heart-rate inputs. The disposition below
          // carries the evidence domains the reviewer accepted.
          pipelineMetrics: recording.guide?.status === "ok" ? buildPipelineMetrics(recording.guide.payload) : null,
          // The stored ACT / BeatAware results; the disposition below carries
          // the reviewer's plugin reviews. The Medaica Report shows both as
          // information in its Evidence panels.
          vendorEvidence: recording.vendorEvidence ?? null,
          disposition: recording.disposition
            ? {
                ...recording.disposition,
                exportValue: deriveDispositionExportValue(
                  recording.disposition.decision,
                  recording.disposition.note,
                  recording.disposition.observations
                ),
              }
            : null,
          evidencePackage,
        }
      })
      const exportRow = await pcgReviewApi.postExport(bundle.examId, {
        payload: {
          schema: "medaica.pcg.exam_review_export",
          version: "0",
          examId: bundle.examId,
          createdAt: new Date().toISOString(),
          examTimingConsistency,
          recordings,
        },
      })
      // POST returns the full stored row; the bundle list keeps summaries —
      // and only gains one when the server actually stored a new export.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { payload: _storedPayload, created, ...exportSummary } = exportRow
      if (created) {
        setBundle((current) => (current ? { ...current, exports: [...current.exports, exportSummary] } : current))
      }
      navigate(`${examsBase}/${bundle.examId}/review/report`, { state: openedFromState(location.pathname) })
    } catch (err) {
      logError(err)
      if (err instanceof PcgExportPayloadRejectedError) {
        // The server's structural gate said no (nothing was stored) —
        // surface WHAT was wrong, not a generic failure.
        enqueueSnackbar(`Export rejected by ${err.schemaVersion}: ${err.errors.join("; ")}`, { variant: "error" })
      } else {
        enqueueSnackbar("There was an error opening the Medaica Report", { variant: "error" })
      }
    } finally {
      setBusy(false)
      setOpeningHeartReport(false)
    }
  }, [
    buildRecordingEvidencePackage,
    bundle,
    enqueueSnackbar,
    examTimingConsistency,
    examsBase,
    location.pathname,
    navigate,
    pcgReviewApi,
  ])

  // "Resend email": the server queues the report-ready email again with a
  // fresh link (the earlier link stops working) and reports the refreshed
  // delivery facts, which replace the ones in the bundle.
  const handleResendEmail = useCallback(async () => {
    if (!bundle) {
      return
    }
    setDeliveryBusy(true)
    try {
      const release = await pcgReviewApi.resendPatientReportEmail(bundle.examId)
      setBundle((current) =>
        current?.heartReport ? { ...current, heartReport: { ...current.heartReport, release } } : current
      )
      enqueueSnackbar("Email re-sent with a fresh link")
    } catch (err) {
      logError(err)
      enqueueSnackbar(resendErrorMessage(err), { variant: "error" })
    } finally {
      setDeliveryBusy(false)
    }
  }, [bundle, enqueueSnackbar, pcgReviewApi])

  // "Download patient report" / "Download provider report": a frozen PDF of
  // the release — the same bytes the patient downloads — handed to the
  // browser. The provider report is the clinician-format document the patient
  // receives to share with their own doctor.
  const handleDownloadReleasedReport = useCallback(
    async (kind: "patient" | "provider") => {
      if (!bundle || !exam) {
        return
      }
      setDeliveryBusy(true)
      try {
        const pdf = await (kind === "provider"
          ? pcgReviewApi.getProviderReportPdf(bundle.examId)
          : pcgReviewApi.getPatientReportPdf(bundle.examId))
        const examDate = moment(bundle.examDateCreated ?? exam.dateCreated).format("YYYY-MM-DD")
        downloadBlob(
          pdf,
          kind === "provider" ? `Medaica-Report-for-your-doctor-${examDate}.pdf` : `Medaica-Report-${examDate}.pdf`
        )
      } catch (err) {
        logError(err)
        enqueueSnackbar(
          err instanceof PcgPatientReportNotReleasedError
            ? "Medaica Report is not released"
            : `Could not download the ${kind} report`,
          { variant: "error" }
        )
      } finally {
        setDeliveryBusy(false)
      }
    },
    [bundle, enqueueSnackbar, exam, pcgReviewApi]
  )

  if (!exam || !bundle) {
    return <Loading />
  }

  const demographics = examDemographics(exam, user.organizationId)
  // Small-A / large-A control, in the viewer's head row between the Tips
  // On / Off toggle and Quick Start (the same order as the handoff viewer's)
  const interfaceSizeToggle = (
    <button
      type="button"
      className="ui-size-toggle control-help"
      aria-pressed={interfaceSizeLarge}
      aria-label="Toggle larger interface text and controls"
      data-hover="Use larger interface text and controls."
      onClick={toggleInterfaceSize}
    >
      <span className="ui-size-small" aria-hidden="true">
        A
      </span>
      <span className="ui-size-large" aria-hidden="true">
        A
      </span>
    </button>
  )
  const release = bundle.heartReport?.status === "released" ? bundle.heartReport.release : null

  return (
    <Page variant="full">
      {/* The way back is the portal's chevron link (as Patient Details has it);
          the page's identity is the workspace's own top bar below. */}
      <Box sx={{ mt: 1, mb: 1.5 }}>
        <Typography
          component={RouterLink}
          to={backToExamLink.to}
          onClick={backToExamLink.onClick}
          aria-label="Back to exam"
          data-testid="back-link"
          sx={backLinkSx}
        >
          <ChevronLeftIcon color="primary" />
        </Typography>
      </Box>
      {/* Handoff v0.8.3 exam layout: sidebar + the viewer's .aptm-wave-main
          column inside one dark panel (pcg-exam-workspace.css). */}
      <div className={`pcg-viewer pcg-exam-workspace${interfaceSizeLarge ? " ui-size-large" : ""}`}>
        <div className="panel">
          {/* The standalone viewer's header row: the brand (portalled here
              from the viewer, which owns its menu) and the exam's facts. */}
          <div className="exam-top-bar">
            <div ref={setBrandContainer} className="exam-top-bar-brand" />
            <ExamFacts demographics={demographics} />
          </div>
          <div className="aptm-wave-layout">
            <aside className="aptm-sidebar">
              {/* APTM Exam Overview — renders nothing for exams that don't fit
                  the A/P/T/M mapping; the Patient Context strip sits inside it
                  (renders nothing without a questionnaire) */}
              <ExamOverviewMap
                recordings={bundle.recordings}
                previewEvidence={bundle.examPreviewEvidence}
                selectedAuscultationId={selectedAuscultationId}
                onSelect={setSelectedAuscultationId}
                tipsEnabled={tipsEnabled}
              >
                <PatientContextStrip questionnaire={bundle.questionnaire} />
              </ExamOverviewMap>
              <PluginRack
                recordingId={selectedAuscultationId}
                recordingLabel={selectedRecording ? recordingSiteLabel(selectedRecording) : "unlabeled"}
                vendorEvidence={selectedRecording?.vendorEvidence}
                pluginReviews={selectedRecording?.disposition?.pluginReviews}
                onReviewChange={handlePluginReview}
                busy={busy}
              />
              {!aptmLayoutFits(bundle.recordings) ? (
                // Non-conforming exams (lung points, `other`, unlabeled)
                // keep the generic recording rail as their navigation surface
                <div className="aptm-sidebar-foot">
                  <RecordingRail
                    recordings={bundle.recordings}
                    selectedAuscultationId={selectedAuscultationId}
                    onSelect={setSelectedAuscultationId}
                    previewEvidence={bundle.examPreviewEvidence}
                  />
                </div>
              ) : null}
            </aside>

            {selectedRecording?.guide?.status === "ok" ? (
              <PcgRecordingViewer
                className="aptm-wave-main"
                payload={selectedRecording.guide.payload}
                turbulentModel={turbulentModels[turbulentModelVersionOf(selectedRecording.guide.payload) ?? ""] ?? null}
                audioSrc={audioBlob}
                recordingId={selectedRecording.auscultationId}
                examTimingConsistency={examTimingConsistency}
                onAudioGraph={setAudioGraph}
                onTooltipsEnabledChange={setTipsEnabled}
                brandContainer={brandContainer}
                recordingHeader={
                  <SelectedRecordingNav
                    recordings={bundle.recordings}
                    selectedAuscultationId={selectedAuscultationId}
                    onSelect={setSelectedAuscultationId}
                  />
                }
                recordingLabel={selectedRecordingLabel}
                recordingHeaderActions={interfaceSizeToggle}
                filterControls={<PcgFilterControls controls={filterControls} />}
                reviewControls={
                  <DispositionControls
                    disposition={selectedRecording.disposition}
                    recordingLabel={selectedRecordingLabel}
                    acceptEvidence={acceptEvidence}
                    onSubmit={(request) => void handleDisposition(request)}
                    busy={busy}
                  />
                }
                secondaryActions={
                  <div className="heart-report-actions">
                    <button
                      className="zoom-btn"
                      type="button"
                      title="Open the Medaica Report for this exam, updated from the current review"
                      disabled={busy}
                      aria-busy={openingHeartReport}
                      onClick={() => void handleOpenHeartReport()}
                    >
                      {openingHeartReport ? <span className="btn-spinner" aria-hidden="true" /> : null}
                      Open Medaica Report
                    </button>
                  </div>
                }
                reviewSummary={
                  <div className="exam-review-summary">
                    <div className="exam-review-summary-lines">
                      {acceptedEvidence ? (
                        <div className="exam-note-summary visible" data-testid="accepted-evidence">
                          Accepted Medaica Report evidence: {acceptedEvidence}
                        </div>
                      ) : null}
                      {examNotes ? (
                        <div className="exam-note-summary visible" data-testid="exam-notes-summary">
                          Exam notes summary: {examNotes}
                        </div>
                      ) : null}
                    </div>
                    <span className="heart-report-status" data-testid="heart-report-status">
                      {heartReportStatusLabel(bundle.heartReport, acceptedEvidence !== null)}
                    </span>
                  </div>
                }
              />
            ) : (
              <div className="aptm-wave-main">
                <div className="panel" style={{ padding: 16 }}>
                  <div className="selected-recording-head">
                    {selectedRecording ? (
                      <SelectedRecordingNav
                        recordings={bundle.recordings}
                        selectedAuscultationId={selectedAuscultationId}
                        onSelect={setSelectedAuscultationId}
                      />
                    ) : null}
                    {interfaceSizeToggle}
                  </div>
                  {!selectedRecording ? (
                    <Alert severity="info">Select a recording</Alert>
                  ) : selectedRecording.guide === null ? (
                    // Reconciled convention: no guide row yet = pipeline pending
                    <Alert severity="info">
                      Guide analysis is still running for this recording. The viewer opens automatically once the PCG
                      pipeline produces its guide.
                    </Alert>
                  ) : (
                    <Alert severity="error">
                      Guide analysis failed for this recording
                      {selectedRecording.guide.errorDetail ? `: ${selectedRecording.guide.errorDetail}` : "."} The raw
                      audio remains available from Exam Details.
                    </Alert>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {bundle.heartReport?.status === "released" ? (
        // The release's delivery facts; a failed email is an error the
        // reviewer should act on (fix the address, re-send)
        <Alert
          severity={release?.emailStatus === "failed" ? "error" : "success"}
          sx={ACTION_STRIP_SX}
          data-testid="patient-report-release-strip"
          action={
            <Stack direction="row" sx={{ flexWrap: "wrap", justifyContent: "flex-end", gap: 1 }}>
              <Button
                color="inherit"
                size="small"
                disabled={deliveryBusy}
                aria-busy={deliveryBusy}
                onClick={() => void handleResendEmail()}
              >
                Resend email
              </Button>
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                disabled={deliveryBusy}
                aria-busy={deliveryBusy}
                onClick={() => void handleDownloadReleasedReport("patient")}
              >
                Download patient report
              </Button>
              {release?.hasProviderReport ? (
                // Only a release that froze a provider report has one to download
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  disabled={deliveryBusy}
                  aria-busy={deliveryBusy}
                  onClick={() => void handleDownloadReleasedReport("provider")}
                >
                  Download provider report
                </Button>
              ) : null}
            </Stack>
          }
        >
          {release ? releaseStripText(release) : "Released to the patient — delivery details are not available"}
        </Alert>
      ) : null}
    </Page>
  )
}

export default ExamReviewView
